/* The caller's own profile. */

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/* Cards hold several fields; this is the gap between them. */
.field + .field {
  margin-top: var(--space-5);
}

.label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text);
}

/*
  A label with a badge beside it — verified, rating.

  *** BESIDE THE LABEL, NOT AT THE FAR EDGE ***
  This was `justify-content: space-between`, which on a wide card threw the
  badge hundreds of pixels from the word it qualifies — "Email" on the left
  and "Not verified" against the right margin read as two unrelated things
  and were simply missed (reported 2026-11-02).
*/
.labelRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/*
  An input and its button. `wrap` rather than shrinking the field: on a
  phone the button belongs under the input, not squeezed beside it.
*/
.row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

/* Inside a row the input takes whatever the button leaves. */
.row .input {
  flex: 1 1 14rem;
}

/*
  *** `flex-basis`, NOT `flex: 1 1 14rem` ***
  The shorthand sets `flex-grow: 1`, which is right inside `.row` — the
  input takes the space the Save button leaves. But the mentor's fields put
  the same class directly in `.field`, a COLUMN container, where growing
  means growing TALL: three inputs rendered about 200px high with their
  text floating in the middle (reported 2026-11-02).

  Growing is a property of the row, so the row asks for it.
*/
.input {
  min-width: 0;
  padding: var(--space-3);
  font: inherit;
  font-size: var(--text-base);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.textarea {
  width: 100%;
  padding: var(--space-3);
  font: inherit;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  resize: vertical;
}

.input:focus,
.textarea:focus {
  outline: 2px solid var(--indigo);
  outline-offset: -1px;
}

.input:disabled {
  color: var(--text-muted);
  background: var(--bg-sunken);
  cursor: not-allowed;
}

/* A value the person cannot change — phone, name, email on a mentor. */
.readOnly {
  margin: 0;
  padding: var(--space-3);
  font-size: var(--text-base);
  color: var(--text);
  background: var(--bg-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.hint {
  margin: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

/* "Send it again" inside a hint — a link, not a button shape. */
.linkButton {
  padding: 0;
  font: inherit;
  font-size: inherit;
  color: var(--indigo);
  background: none;
  border: 0;
  text-decoration: underline;
  cursor: pointer;
}

.linkButton:disabled {
  color: var(--text-subtle);
  cursor: default;
}

.actions {
  display: flex;
  justify-content: flex-start;
  margin-top: var(--space-5);
}

/* The code box, set apart from the field that triggered it. */
.verify {
  margin-top: var(--space-5);
  padding-top: var(--space-5);
  border-top: 1px solid var(--border);
}

/*
  *** WHAT IS STOPPING THEM ***
  An invisible mentor gets no bookings and no explanation. Amber, not red:
  nothing is broken and nothing was lost — there is just something to do.
*/
.blocked {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4) var(--space-5);
  background: var(--warning-soft);
  border: 1px solid var(--warning);
  border-radius: var(--radius-lg);
}

.blockedTitle {
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
}

.blockedText {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text);
}

/* ------------------------------------------------------ subject area */

.categoryGrid {
  display: grid;
  gap: var(--space-2);
  /* Capped, so one subject is a card rather than a banner across a wide
     screen — the same rule as the mentor and plan grids. */
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  margin-top: var(--space-2);
}

.category {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3) var(--space-4);
  font: inherit;
  text-align: left;
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.category:hover:not(:disabled) {
  border-color: var(--border-strong);
}

.category:disabled {
  cursor: wait;
}

/* Outlined rather than filled: the description must stay readable, the
   same choice as the mentor card in the booking funnel. */
.categoryOn {
  border-color: var(--indigo);
  box-shadow: 0 0 0 1px var(--indigo);
}

.categoryName {
  font-size: var(--text-base);
  font-weight: 600;
}

.categoryDesc {
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

.muted {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.error {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-sm);
}
